import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  FileSignature, Plus, Pencil, Trash2, Upload, FileText, CalendarCheck2,
  CalendarClock, AlertTriangle, CheckCircle2, Search, Printer, Loader2, Phone, Share2,
  Image as ImageIcon,
} from "lucide-react";

const CATEGORY_LABEL = { biomed: "أجهزة طبية", electrical: "أجهزة كهربائية", other: "أخرى" };
const fmtMonth = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("ar-u-nu-latn", { month: "long", year: "numeric" }) : "—";
const STATUS_BADGE = {
  active: { label: "نشط", cls: "bg-emerald-100 text-emerald-800" },
  expiring: { label: "قارب الانتهاء", cls: "bg-amber-100 text-amber-800" },
  expired: { label: "منتهي", cls: "bg-red-100 text-red-700" },
};
const emptyForm = {
  company_name: "", contact_person: "", contact_phone: "", category: "biomed",
  start_date: "", end_date: "", annual_visits: 2,
  internal_responsible_name: "", internal_responsible_phone: "", notes: "",
};
const emptyConfirm = { actual_date: new Date().toISOString().slice(0, 10), technician_name: "", work_done: "", notes: "" };

export default function PPMContracts() {
  const { user, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const canManage = user?.role === "admin" || user?.role === "maintenance_manager" || user?.is_biomed_officer || user?.is_ppm_officer;
  const isAdmin = user?.role === "admin";

  const [contracts, setContracts] = useState([]);
  const [search, setSearch] = useState("");
  const [formDialog, setFormDialog] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pdfFile, setPdfFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [confirmVisit, setConfirmVisit] = useState(null);
  const [confirmForm, setConfirmForm] = useState(emptyConfirm);
  const [reportFile, setReportFile] = useState(null);
  const [extraDate, setExtraDate] = useState("");
  const [editVisitId, setEditVisitId] = useState(null);
  const [editMonth, setEditMonth] = useState("");
  const [printItem, setPrintItem] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/ppm-contracts`, { headers });
      setContracts(res.data);
    } catch { toast.error("فشل تحميل العقود"); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contracts;
    return contracts.filter(c =>
      (c.contract_no || "").toLowerCase().includes(q) ||
      (c.company_name || "").toLowerCase().includes(q) ||
      (c.contact_person || "").toLowerCase().includes(q)
    );
  }, [contracts, search]);

  const kpis = useMemo(() => {
    const monthEnd = new Date(); monthEnd.setDate(monthEnd.getDate() + 30);
    const today = new Date().toISOString().slice(0, 10);
    const horizon = monthEnd.toISOString().slice(0, 10);
    let month = 0, overdue = 0;
    contracts.forEach(c => {
      overdue += c.visits_overdue || 0;
      (c.visits || []).forEach(v => {
        if (v.status === "scheduled" && v.scheduled_date >= today && v.scheduled_date <= horizon) month++;
      });
    });
    return {
      active: contracts.filter(c => c.contract_status === "active").length,
      expiring: contracts.filter(c => c.contract_status === "expiring").length,
      month, overdue,
    };
  }, [contracts]);

  const openCreate = () => { setForm(emptyForm); setPdfFile(null); setFormDialog({ mode: "create" }); };
  const openEdit = (c) => {
    setForm({
      company_name: c.company_name, contact_person: c.contact_person || "", contact_phone: c.contact_phone || "",
      category: c.category, start_date: c.start_date, end_date: c.end_date, annual_visits: c.annual_visits,
      internal_responsible_name: c.internal_responsible_name || "", internal_responsible_phone: c.internal_responsible_phone || "",
      notes: c.notes || "",
    });
    setPdfFile(null);
    setFormDialog({ mode: "edit", id: c.id });
  };

  const saveContract = async () => {
    if (!form.company_name.trim()) { toast.error("اسم جهة الصيانة مطلوب"); return; }
    if (!form.start_date || !form.end_date) { toast.error("تواريخ العقد مطلوبة"); return; }
    setBusy(true);
    try {
      let saved;
      if (formDialog.mode === "create") {
        saved = (await axios.post(`${API}/ppm-contracts`, { ...form, annual_visits: Number(form.annual_visits) }, { headers })).data;
        toast.success(`تم إنشاء العقد ${saved.contract_no} مع ${saved.visits_total} زيارة مجدولة`);
      } else {
        saved = (await axios.put(`${API}/ppm-contracts/${formDialog.id}`, { ...form, annual_visits: Number(form.annual_visits) }, { headers })).data;
        toast.success("تم تحديث العقد");
      }
      if (pdfFile) {
        const fd = new FormData(); fd.append("file", pdfFile);
        await axios.post(`${API}/ppm-contracts/${saved.id}/pdf`, fd, { headers });
      }
      setFormDialog(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الحفظ"); }
    finally { setBusy(false); }
  };

  const deleteContract = async (c) => {
    if (!window.confirm(`حذف العقد ${c.contract_no} — ${c.company_name}؟`)) return;
    try {
      await axios.delete(`${API}/ppm-contracts/${c.id}`, { headers });
      toast.success("تم حذف العقد");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الحذف"); }
  };

  const openPdf = async (c) => {
    try {
      const res = await axios.get(`${API}/ppm-contracts/${c.id}/pdf`, { headers, responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("لا يوجد ملف عقد مرفق"); }
  };

  const openReport = async (cid, vid) => {
    try {
      const res = await axios.get(`${API}/ppm-contracts/${cid}/visits/${vid}/report`, { headers, responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("لا يوجد تقرير مرفق"); }
  };

  const openPhoto = async (cid, vid, idx) => {
    try {
      const res = await axios.get(`${API}/ppm-contracts/${cid}/visits/${vid}/photos/${idx}`, { headers, responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("لا توجد صورة"); }
  };

  const openFullReport = async (cid, vid) => {
    try {
      toast.info("جاري تجهيز تقرير الزيارة...");
      const res = await axios.get(`${API}/ppm-contracts/${cid}/visits/${vid}/full-report`, { headers, responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("فشل تجهيز تقرير الزيارة"); }
  };

  const submitConfirm = async () => {
    if (!confirmForm.technician_name.trim() || !confirmForm.work_done.trim()) {
      toast.error("اسم المسؤول وما تم عمله مطلوبان"); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("actual_date", confirmForm.actual_date);
      fd.append("technician_name", confirmForm.technician_name);
      fd.append("work_done", confirmForm.work_done);
      fd.append("notes", confirmForm.notes);
      if (reportFile) fd.append("file", reportFile);
      const res = await axios.post(`${API}/ppm-contracts/${detail.id}/visits/${confirmVisit.id}/confirm`, fd, { headers });
      toast.success("تم توثيق الزيارة بنجاح");
      setConfirmVisit(null); setDetail(res.data); load();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل التوثيق"); }
    finally { setBusy(false); }
  };

  const addExtraVisit = async () => {
    if (!extraDate) { toast.error("حدد تاريخ الزيارة"); return; }
    try {
      const res = await axios.post(`${API}/ppm-contracts/${detail.id}/visits`, { scheduled_date: extraDate }, { headers });
      toast.success("تمت إضافة زيارة استثنائية");
      setExtraDate(""); setDetail(res.data); load();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الإضافة"); }
  };

  const saveVisitDate = async (v) => {
    if (!editMonth) { toast.error("حدد الشهر الجديد"); return; }
    const day = (v.scheduled_date || "").slice(8, 10) || "15";
    const newDate = `${editMonth}-${Math.min(parseInt(day, 10) || 15, 28).toString().padStart(2, "0")}`;
    try {
      const res = await axios.put(`${API}/ppm-contracts/${detail.id}/visits/${v.id}`, { scheduled_date: newDate }, { headers });
      toast.success("تم تعديل موعد الزيارة");
      setEditVisitId(null); setEditMonth(""); setDetail(res.data); load();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل التعديل"); }
  };

  const deleteVisit = async (vid) => {
    if (!window.confirm("حذف هذه الزيارة المجدولة؟")) return;
    try {
      const res = await axios.delete(`${API}/ppm-contracts/${detail.id}/visits/${vid}`, { headers });
      toast.success("تم حذف الزيارة");
      setDetail(res.data); load();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الحذف"); }
  };

  const sendVisitLink = async (v) => {
    try {
      const res = await axios.post(`${API}/ppm-contracts/${detail.id}/visits/${v.id}/share-link`, {}, { headers });
      try { await navigator.clipboard.writeText(res.data.link); } catch { /* clipboard unavailable */ }
      toast.success(res.data.whatsapp_sent ? "أُرسل رابط التوثيق عبر واتساب ونُسخ للحافظة" : "نُسخ الرابط للحافظة (لم يُرسل واتساب — تحقق من رقم العقد)");
    } catch (e) { toast.error(e.response?.data?.detail || "فشل إنشاء الرابط"); }
  };

  const openSignature = async (cid, vid) => {
    try {
      const res = await axios.get(`${API}/ppm-contracts/${cid}/visits/${vid}/signature`, { headers, responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("لا يوجد توقيع"); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const visitStatus = (v) => v.status === "done"
    ? { label: "تمت", cls: "bg-emerald-100 text-emerald-800" }
    : v.scheduled_date < today
      ? { label: "متأخرة", cls: "bg-red-100 text-red-700" }
      : { label: "مجدولة", cls: "bg-sky-100 text-sky-800" };

  return (
    <div className="space-y-6" data-testid="ppm-contracts-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#655dc6] flex items-center justify-center">
            <FileSignature className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">عقود الصيانة الدورية</h1>
            <p className="text-sm text-slate-500">إدارة عقود PPM وتوثيق زيارات الفحص والصيانة</p>
          </div>
        </div>
        {canManage && (
          <Button onClick={openCreate} className="bg-[#655dc6] hover:bg-[#5449a8] text-white gap-1.5" data-testid="add-contract-btn">
            <Plus className="w-4 h-4" /> إضافة عقد
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "عقود نشطة", value: kpis.active, icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50" },
          { label: "قاربت على الانتهاء", value: kpis.expiring, icon: AlertTriangle, cls: "text-amber-600 bg-amber-50" },
          { label: "زيارات خلال 30 يوم", value: kpis.month, icon: CalendarClock, cls: "text-sky-600 bg-sky-50" },
          { label: "زيارات متأخرة", value: kpis.overdue, icon: CalendarCheck2, cls: "text-red-600 bg-red-50" },
        ].map(k => (
          <div key={k.label} className="bg-white border rounded-xl p-4 flex items-center justify-between" data-testid={`kpi-${k.label}`}>
            <div>
              <p className="text-xs text-slate-500 mb-1">{k.label}</p>
              <p className="text-2xl font-bold text-slate-900">{k.value}</p>
            </div>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${k.cls}`}><k.icon className="w-5 h-5" /></div>
          </div>
        ))}
      </div>

      <div className="relative max-w-md">
        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="بحث برقم العقد / الشركة / المسؤول..." className="ps-9" data-testid="contract-search" />
      </div>

      <div className="bg-white border rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-right">رقم العقد</TableHead>
              <TableHead className="text-right">جهة الصيانة</TableHead>
              <TableHead className="text-right">الفئة</TableHead>
              <TableHead className="text-center">المدة</TableHead>
              <TableHead className="text-right">الزيارات</TableHead>
              <TableHead className="text-right">الزيارة القادمة</TableHead>
              <TableHead className="text-right">الحالة</TableHead>
              <TableHead className="text-right">إجراءات</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">لا توجد عقود مسجّلة</TableCell></TableRow>
            )}
            {filtered.map(c => (
              <TableRow key={c.id} data-testid={`contract-row-${c.contract_no}`}>
                <TableCell className="font-mono font-bold text-[#655dc6]">{c.contract_no}</TableCell>
                <TableCell>
                  <p className="font-semibold">{c.company_name}</p>
                  {c.contact_person && <p className="text-xs text-slate-400">{c.contact_person}</p>}
                </TableCell>
                <TableCell><Badge variant="outline">{CATEGORY_LABEL[c.category]}</Badge></TableCell>
                <TableCell className="font-mono text-xs text-center"><span dir="ltr">{c.start_date} → {c.end_date}</span></TableCell>
                <TableCell>
                  <span className="font-bold text-emerald-700">{c.visits_done}</span>
                  <span className="text-slate-400"> / {c.visits_total}</span>
                  {c.visits_overdue > 0 && <Badge className="ms-2 bg-red-100 text-red-700 border-0">{c.visits_overdue} متأخرة</Badge>}
                </TableCell>
                <TableCell className="text-xs">{c.next_visit_date ? fmtMonth(c.next_visit_date) : "—"}</TableCell>
                <TableCell><Badge className={`border-0 ${STATUS_BADGE[c.contract_status].cls}`}>{STATUS_BADGE[c.contract_status].label}</Badge></TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setDetail(c)} data-testid={`view-contract-${c.contract_no}`}>الزيارات</Button>
                    {c.contract_pdf_path && (
                      <Button size="sm" variant="ghost" onClick={() => openPdf(c)} title="ملف العقد" className="text-emerald-700" data-testid={`pdf-contract-${c.contract_no}`}>
                        <FileText className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setPrintItem(c)} title="طباعة السجل A4" data-testid={`print-contract-${c.contract_no}`}>
                      <Printer className="w-4 h-4" />
                    </Button>
                    {canManage && (
                      <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-contract-${c.contract_no}`}><Pencil className="w-4 h-4" /></Button>
                    )}
                    {isAdmin && (
                      <Button size="sm" variant="ghost" onClick={() => deleteContract(c)} className="text-red-600 ms-auto" data-testid={`delete-contract-${c.contract_no}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create / Edit contract */}
      <Dialog open={!!formDialog} onOpenChange={(o) => !o && setFormDialog(null)}>
        <DialogContent dir="rtl" className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{formDialog?.mode === "create" ? "إضافة عقد صيانة دورية" : "تعديل العقد"}</DialogTitle>
            <DialogDescription>عند الإنشاء يولّد النظام الزيارات المجدولة تلقائياً حسب عدد الزيارات السنوية</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>جهة الصيانة (الشركة / الوكيل المعتمد) *</Label>
              <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="f-company" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>اسم المسؤول لديهم</Label>
                <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="f-contact-person" />
              </div>
              <div className="space-y-1">
                <Label>واتساب التذكير (رقم واحد للعقد)</Label>
                <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+9665XXXXXXXX" dir="ltr" data-testid="f-contact-phone" />
              </div>
              <div className="space-y-1">
                <Label>الفئة *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="f-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="biomed">أجهزة طبية</SelectItem>
                    <SelectItem value="electrical">أجهزة كهربائية</SelectItem>
                    <SelectItem value="other">أخرى</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>عدد الزيارات السنوية *</Label>
                <Input type="number" min={1} max={52} value={form.annual_visits} onChange={(e) => setForm({ ...form, annual_visits: e.target.value })} data-testid="f-annual-visits" />
              </div>
              <div className="space-y-1">
                <Label>بداية العقد *</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="f-start" />
              </div>
              <div className="space-y-1">
                <Label>نهاية العقد *</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="f-end" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>ملاحظات</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="min-h-[60px]" data-testid="f-notes" />
            </div>
            <div className="space-y-1">
              <Label>نسخة العقد PDF (اختياري)</Label>
              <Input type="file" accept="application/pdf" onChange={(e) => setPdfFile(e.target.files?.[0] || null)} data-testid="f-pdf" />
            </div>
            {formDialog?.mode === "edit" && (
              <p className="text-xs text-sky-600">عند تعديل التواريخ أو عدد الزيارات: يُعاد جدولة الزيارات غير الموثقة تلقائياً — الزيارات الموثقة والاستثنائية تبقى كما هي</p>
            )}
            <Button onClick={saveContract} disabled={busy} className="w-full bg-[#655dc6] hover:bg-[#5449a8] text-white" data-testid="save-contract-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : formDialog?.mode === "create" ? "إنشاء العقد وجدولة الزيارات" : "حفظ التعديلات"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contract detail + visits */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent dir="rtl" className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-[#655dc6]" />
              {detail?.contract_no} — {detail?.company_name}
            </DialogTitle>
            <DialogDescription>
              {CATEGORY_LABEL[detail?.category]} · {detail?.start_date} → {detail?.end_date} · {detail?.annual_visits} زيارة/سنة
              {detail?.contact_person && <> · {detail.contact_person} {detail.contact_phone && <span dir="ltr">({detail.contact_phone})</span>}</>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-600">الزيارات ({detail?.visits_done}/{detail?.visits_total} منفذة)</p>
              {canManage && (
                <div className="flex items-center gap-2">
                  <Input type="date" value={extraDate} onChange={(e) => setExtraDate(e.target.value)} className="h-8 w-36" data-testid="extra-visit-date" />
                  <Button size="sm" variant="outline" onClick={addExtraVisit} className="h-8 text-xs" data-testid="add-extra-visit-btn">+ زيارة استثنائية</Button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              {(detail?.visits || []).map(v => {
                const st = visitStatus(v);
                return (
                  <div key={v.id} className={`border rounded-lg p-3 text-sm ${v.status === "done" ? "bg-emerald-50/50" : v.scheduled_date < today ? "bg-red-50/50" : "bg-white"}`} data-testid={`visit-${v.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-700">زيارة {v.seq}</span>
                        {v.exceptional && <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px]">استثنائية</Badge>}
                        {v.source === "contractor" && <Badge className="bg-indigo-100 text-indigo-700 border-0 text-[10px]">وثّقها المقاول</Badge>}
                        <Badge className={`border-0 ${st.cls}`}>{st.label}</Badge>
                        <span className="text-xs text-slate-500">{fmtMonth(v.scheduled_date)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {v.status === "done" && (
                          <Button size="sm" variant="ghost" onClick={() => openFullReport(detail.id, v.id)} className="text-[#655dc6] h-7 text-xs gap-1" title="طباعة تقرير الزيارة بمرفقاته وتوقيع المقاول" data-testid={`visit-print-${v.id}`}>
                            <Printer className="w-3.5 h-3.5" /> طباعة الزيارة
                          </Button>
                        )}
                        {v.status === "done" && v.report_pdf_path && (
                          <Button size="sm" variant="ghost" onClick={() => openReport(detail.id, v.id)} className="text-emerald-700 h-7 text-xs gap-1" data-testid={`visit-report-${v.id}`}>
                            <FileText className="w-3.5 h-3.5" /> التقرير
                          </Button>
                        )}
                        {v.status === "done" && (v.photo_paths || []).map((_, i) => (
                          <Button key={i} size="sm" variant="ghost" onClick={() => openPhoto(detail.id, v.id, i)} className="text-sky-700 h-7 text-xs gap-1" data-testid={`visit-photo-${v.id}-${i}`}>
                            <ImageIcon className="w-3.5 h-3.5" /> صورة {i + 1}
                          </Button>
                        ))}
                        {v.status === "done" && v.signature_path && (
                          <Button size="sm" variant="ghost" onClick={() => openSignature(detail.id, v.id)} className="text-purple-700 h-7 text-xs gap-1" data-testid={`visit-signature-${v.id}`}>
                            <FileSignature className="w-3.5 h-3.5" /> التوقيع
                          </Button>
                        )}
                        {v.status === "scheduled" && canManage && editVisitId === v.id && (
                          <>
                            <Input type="month" value={editMonth} onChange={(e) => setEditMonth(e.target.value)} className="h-7 w-36 text-xs" data-testid={`edit-visit-month-${v.id}`} />
                            <Button size="sm" onClick={() => saveVisitDate(v)} className="bg-[#655dc6] hover:bg-[#5449a8] text-white h-7 text-xs" data-testid={`save-visit-date-${v.id}`}>حفظ</Button>
                            <Button size="sm" variant="ghost" onClick={() => { setEditVisitId(null); setEditMonth(""); }} className="h-7 text-xs text-slate-500">إلغاء</Button>
                          </>
                        )}
                        {v.status === "scheduled" && canManage && editVisitId !== v.id && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => { setEditVisitId(v.id); setEditMonth((v.scheduled_date || "").slice(0, 7)); }} className="h-7 text-xs gap-1 text-slate-600" title="تعديل موعد الزيارة" data-testid={`edit-visit-${v.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => sendVisitLink(v)} className="h-7 text-xs gap-1 border-[#655dc6]/40 text-[#655dc6]" title="إرسال رابط التوثيق للمقاول عبر واتساب" data-testid={`share-visit-${v.id}`}>
                              <Share2 className="w-3.5 h-3.5" /> رابط للمقاول
                            </Button>
                            <Button size="sm" onClick={() => { setConfirmVisit(v); setConfirmForm(emptyConfirm); setReportFile(null); }} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" data-testid={`confirm-visit-${v.id}`}>
                              تأكيد الزيارة
                            </Button>
                          </>
                        )}
                        {v.status === "scheduled" && isAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => deleteVisit(v.id)} className="text-red-600 h-7"><Trash2 className="w-3.5 h-3.5" /></Button>
                        )}
                      </div>
                    </div>
                    {v.status === "done" && (
                      <div className="mt-2 pt-2 border-t text-xs text-slate-600 space-y-0.5">
                        <p><span className="text-slate-400">تمت بتاريخ:</span> <span className="font-mono">{v.actual_date}</span> · <span className="text-slate-400">المسؤول:</span> {v.technician_name}</p>
                        <p><span className="text-slate-400">ما تم عمله:</span> {v.work_done}</p>
                        {v.notes && <p><span className="text-slate-400">ملاحظات:</span> {v.notes}</p>}
                        <p className="text-slate-400">وثّقها: {(v.confirmed_by_name || "").replace(" (المقاول)", "")}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm visit */}
      <Dialog open={!!confirmVisit} onOpenChange={(o) => !o && setConfirmVisit(null)}>
        <DialogContent dir="rtl" className="max-w-md">
          <DialogHeader>
            <DialogTitle>توثيق زيارة الصيانة الدورية</DialogTitle>
            <DialogDescription>زيارة {confirmVisit?.seq} — مجدولة في {confirmVisit && fmtMonth(confirmVisit.scheduled_date)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>تاريخ الزيارة الفعلي *</Label>
                <Input type="date" value={confirmForm.actual_date} onChange={(e) => setConfirmForm({ ...confirmForm, actual_date: e.target.value })} data-testid="cv-date" />
              </div>
              <div className="space-y-1">
                <Label>اسم المسؤول *</Label>
                <Input value={confirmForm.technician_name} onChange={(e) => setConfirmForm({ ...confirmForm, technician_name: e.target.value })} data-testid="cv-technician" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>ما تم فحصه وعمله *</Label>
              <Textarea value={confirmForm.work_done} onChange={(e) => setConfirmForm({ ...confirmForm, work_done: e.target.value })} className="min-h-[80px]" placeholder="تفاصيل الفحص والصيانة المنفذة..." data-testid="cv-work" />
            </div>
            <div className="space-y-1">
              <Label>ملاحظات</Label>
              <Textarea value={confirmForm.notes} onChange={(e) => setConfirmForm({ ...confirmForm, notes: e.target.value })} className="min-h-[50px]" data-testid="cv-notes" />
            </div>
            <div className="space-y-1">
              <Label>تقرير الزيارة PDF (اختياري)</Label>
              <Input type="file" accept="application/pdf" onChange={(e) => setReportFile(e.target.files?.[0] || null)} data-testid="cv-report" />
            </div>
            <Button onClick={submitConfirm} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cv-save-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "توثيق الزيارة"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* A4 print log */}
      <Dialog open={!!printItem} onOpenChange={(o) => !o && setPrintItem(null)}>
        <DialogContent dir="rtl" className="max-w-2xl max-h-[90vh] overflow-y-auto ppm-print-dialog">
          <DialogHeader>
            <DialogTitle>طباعة سجل زيارات العقد</DialogTitle>
            <DialogDescription>ورقة A4 بسجل الزيارات الموثقة</DialogDescription>
          </DialogHeader>
          <style>{`
            .ppm-sheet { background:#fff; color:#0A0A0A; font-size:12px; }
            .ppm-sheet .ph { display:flex; align-items:center; justify-content:space-between; border-bottom:2px solid #655dc6; padding-bottom:8px; margin-bottom:10px; }
            .ppm-sheet .ph img { width:52px; height:52px; object-fit:contain; }
            .ppm-sheet table { width:100%; border-collapse:collapse; margin-bottom:10px; }
            .ppm-sheet td, .ppm-sheet th { border:1px solid #CBD5E1; padding:5px 8px; text-align:right; }
            .ppm-sheet th { background:#F1F5F9; font-weight:700; }
            .ppm-sheet .k { background:#F8FAFC; font-weight:600; width:130px; }
            .ppm-sheet .sec { font-weight:800; color:#655dc6; margin:8px 0 6px; }
            .ppm-sheet .mono { font-family:'Courier New',monospace; direction:ltr; text-align:left; }
            @media print {
              @page { size: A4 portrait; margin: 12mm; }
              #root { display:none !important; }
              html, body { margin:0 !important; padding:0 !important; }
              body * { visibility: hidden !important; }
              .ppm-sheet, .ppm-sheet * { visibility: visible !important; }
              .ppm-print-dialog { position: static !important; transform:none !important; width:100% !important; max-width:100% !important; max-height:none !important; overflow:visible !important; border:none !important; box-shadow:none !important; padding:0 !important; }
            }
          `}</style>
          {printItem && (
            <div className="ppm-sheet">
              <div className="ph">
                <div>
                  <p style={{ fontWeight: 800, fontSize: 15 }}>سجل زيارات الصيانة الدورية — PPM Visits Log</p>
                  <p style={{ color: "#64748B", fontSize: 11 }}>شركة مركز لوسنت للأسنان</p>
                </div>
                <img src="/lucent-logo.png" alt="Lucent" />
              </div>
              <table>
                <tbody>
                  <tr><td className="k">رقم العقد</td><td className="mono" style={{ fontWeight: 700 }}>{printItem.contract_no}</td></tr>
                  <tr><td className="k">جهة الصيانة</td><td>{printItem.company_name}{printItem.contact_person ? ` — ${printItem.contact_person}` : ""}</td></tr>
                  <tr><td className="k">الفئة</td><td>{CATEGORY_LABEL[printItem.category]}</td></tr>
                  <tr><td className="k">مدة العقد</td><td className="mono">{printItem.start_date} → {printItem.end_date}</td></tr>
                  <tr><td className="k">الزيارات</td><td>{printItem.visits_done} منفذة من {printItem.visits_total} ({printItem.annual_visits} زيارة/سنة)</td></tr>
                </tbody>
              </table>
              <div className="sec">الزيارات ({printItem.visits?.length || 0})</div>
              <table>
                <thead>
                  <tr><th>#</th><th>الموعد</th><th>الحالة</th><th>التاريخ الفعلي</th><th>المسؤول</th><th>ما تم عمله</th><th>وثّقها</th></tr>
                </thead>
                <tbody>
                  {(printItem.visits || []).map(v => (
                    <tr key={v.id}>
                      <td>{v.seq}{v.exceptional ? " *" : ""}</td>
                      <td>{fmtMonth(v.scheduled_date)}</td>
                      <td style={{ fontWeight: 700 }}>{v.status === "done" ? "تمت" : v.scheduled_date < today ? "متأخرة" : "مجدولة"}</td>
                      <td className="mono">{v.actual_date || "—"}</td>
                      <td>{v.technician_name || "—"}</td>
                      <td>{v.work_done || "—"}</td>
                      <td>{(v.confirmed_by_name || "—").replace(" (المقاول)", "")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: 10, color: "#64748B" }}>* زيارة استثنائية خارج الجدول الأساسي — طُبع بتاريخ {new Date().toLocaleDateString("en-CA")}</p>
            </div>
          )}
          <Button onClick={() => window.print()} className="w-full bg-[#655dc6] hover:bg-[#5449a8] text-white gap-2" data-testid="print-ppm-log-btn">
            <Printer className="w-4 h-4" /> طباعة A4
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
